import React from "react";
import { Link } from "react-router-dom";

/**
 * The staff area's pieces, in the app's own visual language (the carrier board's ink, hairlines and
 * mono labels). Our admin is MuleWorks-only: the carrier never sees these screens, only the data
 * they set up (Brian, 2026-10-30: "It doesn't live anywhere on the client's dashboard. Only the
 * data is used to setup the account.").
 */

// ink3 is darker than the carrier board's (#8C8C8C, about 3.4:1 on white): small grey text here
// carries real information, so it meets WCAG AA (about 5.3:1).
export const T = {
  ink: "#000000", ink2: "#5A5A5A", ink3: "#6B6B6B", ink4: "#BFBFBF",
  hair: "#DCDCDC", hairSoft: "#EDEDED", wash: "#F5F5F5", surface: "#FFFFFF",
};
export const sans = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif`;
export const mono = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;

export const STAGE_LABEL = { setup: "In setup", rehearsal: "Rehearsal", live: "Live", paused: "Paused" };

/** What went wrong, in the server's words when it gave any. */
export const errText = (e) => e?.body?.message || e?.message || "That did not work.";

/**
 * A mail error, said honestly. ⛔ A TIMEOUT IS NOT "NOT SENT" (review): the provider may have taken
 * it, and saying it failed invites a second copy.
 */
export const mailError = (error) => (error === "timed_out_may_have_sent"
  ? "the mail provider did not answer in time, so it may have gone. Check the inbox before sending it again"
  : error);

export const fmtPhone = (e164) => {
  const d = String(e164 || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(e164 || "");
};
export const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

export function StaffShell({ active, children }) {
  return (
    <div style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        body { margin: 0; }
        .stf-btn:focus-visible, .stf-link:focus-visible, .stf-input:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
        .stf-row:hover { background: #FAFAFA; }
      `}</style>
      <header style={{ borderBottom: `1px solid ${T.hair}`, background: "#fff" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 22px", minHeight: 54, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <Link to="/" style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4, color: "#000", textDecoration: "none" }}>Alfred</Link>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>MuleWorks admin</span>
          <nav aria-label="Admin" style={{ marginLeft: "auto", display: "flex", gap: 18, fontSize: 13.5, fontWeight: 500 }}>
            <Link to="/staff" className="stf-link" aria-current={active === "carriers" ? "page" : undefined}
              style={{ color: active === "carriers" ? T.ink : T.ink2, textDecoration: active === "carriers" ? "underline" : "none", textUnderlineOffset: 4 }}>Carriers</Link>
            <Link to="/staff/setup-email" className="stf-link" aria-current={active === "email" ? "page" : undefined}
              style={{ color: active === "email" ? T.ink : T.ink2, textDecoration: active === "email" ? "underline" : "none", textUnderlineOffset: 4 }}>Setup email</Link>
          </nav>
        </div>
      </header>
      <main style={{ maxWidth: 960, margin: "0 auto", padding: "28px 22px 70px", display: "grid", gap: 20 }}>{children}</main>
    </div>
  );
}

export function Panel({ title, note, right, children, id }) {
  return (
    <section aria-labelledby={id} style={{ background: T.surface, border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
      {(title || right) && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "16px 20px 12px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            {title && <h2 id={id} style={{ margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>{title}</h2>}
            {note && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>{note}</div>}
          </div>
          {right}
        </div>
      )}
      <div style={{ padding: title || right ? "0 20px 18px" : "18px 20px" }}>{children}</div>
    </section>
  );
}

export function Button({ children, onClick, kind = "primary", disabled, size = "md", type = "button", title }) {
  const k = { primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` }, quiet: { background: "#fff", color: T.ink, border: `1px solid ${T.hair}` } }[kind];
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className="stf-btn" style={{
      ...k, font: "inherit", fontSize: size === "sm" ? 12.5 : 13.5, fontWeight: 500, letterSpacing: -0.15, minHeight: size === "sm" ? 32 : 38,
      padding: size === "sm" ? "5px 11px" : "8px 15px", borderRadius: 7, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1 }}>
      {children}
    </button>
  );
}

export function Micro({ children, style }) {
  return <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: T.ink3, ...style }}>{children}</div>;
}

export function Tag({ children, solid }) {
  return <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.ink}`,
    background: solid ? T.ink : "transparent", color: solid ? "#fff" : T.ink, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
}

const inputBox = { font: "inherit", fontSize: 14, color: T.ink, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 7,
  padding: "8px 10px", boxSizing: "border-box", width: "100%" };

export function Input({ label, value, onChange, type = "text", placeholder, width, id, autoComplete }) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: 12.5, color: T.ink2, width: width || "100%", minWidth: 0 }}>
      {label}
      <input id={id} className="stf-input" type={type} value={value ?? ""} placeholder={placeholder} autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)} style={inputBox} />
    </label>
  );
}

export function TextArea({ label, value, onChange, rows = 3, placeholder }) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: 12.5, color: T.ink2 }}>
      {label}
      <textarea className="stf-input" rows={rows} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        style={{ ...inputBox, resize: "vertical", lineHeight: 1.45 }} />
    </label>
  );
}

export function Select({ label, value, onChange, options, width }) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: 12.5, color: T.ink2, width: width || "auto" }}>
      {label}
      <select className="stf-input" value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputBox, cursor: "pointer" }}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </label>
  );
}

/** A message under an action: what the server said, plainly. Polite, so a screen reader hears it. */
export function Note({ msg }) {
  // Always mounted, so a message that appears in it is announced: a live region created together
  // with its text often is not.
  return (
    <div role="status" aria-live="polite" style={{ fontSize: 12.5, color: msg?.ok ? T.ink3 : T.ink, fontWeight: msg && !msg.ok ? 500 : 400, lineHeight: 1.45 }}>
      {msg ? msg.text : null}
    </div>
  );
}

/** Answers received, out of the questions asked. */
export function Meter({ value, max, label }) {
  const pct = max ? Math.round((value / max) * 1000) / 10 : 0;
  return (
    <div role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}
      style={{ height: 8, borderRadius: 4, background: T.hairSoft, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: T.ink, borderRadius: 4 }} />
    </div>
  );
}
